import { useState } from "react";
import { TrashIcon, Edit3Icon, RotateCcw, RotateCcwIcon, ClipboardListIcon, EyeIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { delay, GenerateSystemNumber, ToastMessage, useDebounce } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";

import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { useBulkDeletePatientsMutation, useBulkDestroyPatientsMutation, useBulkRestorePatientsMutation, useDeletePatientMutation, useDestroyPatientMutation, useFetchPatientsQuery, useFetchTrashedPatientsQuery, useRestorePatientMutation } from "../../../stores/api/patient.api";
import FilterForm from "./Components/FilterForm";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};
const PatientList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [showFilter, setShowFilter] = useState(false)
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [filtersData, setFiltersData] = useState({
    district_id: null,
    unit_id: null,
    taluka_id: null,
    physician_id: null,
    reg_date: null,
    patient_type: null,
    ward_type: null,
    blood_group: null,
  });
  const [appliedFilters, setAppliedFilters] = useState({})
  const debouncedSearch = useDebounce(search, 500)
  const queryParams = payloadHelper.forList({
    page,
    limit,
    search: debouncedSearch,
    filters: JSON.stringify(appliedFilters),
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });
  const activeQuery = useFetchPatientsQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedPatientsQuery(queryParams, { skip: !trashed });
  const { data: patients = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;
  const [deletePatient] = useDeletePatientMutation();
  const [destroyPatient] = useDestroyPatientMutation();
  const [restorePatient] = useRestorePatientMutation();
  const [bulkDeletePatients] = useBulkDeletePatientsMutation();
  const [bulkDestroyPatients] = useBulkDestroyPatientsMutation();
  const [bulkRestorePatients] = useBulkRestorePatientsMutation();
  const resetPage = () => setPage(1);
  const handleFilter = (e) => {
    e.preventDefault();
    resetPage();
    setAppliedFilters(filtersData)
  };
  const handleReset = () => {
    const empty = {
      district_id: "",
      unit_id: "",
      taluka_id: "",
      physician_id: "",
      reg_date: "",
      patient_type: "",
      ward_type: "",
      blood_group: "",
    };
    setAppliedFilters(empty)
    resetPage();
    setFiltersData(empty);

  };
  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} this Patient?`,
      title: `Unit ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restorePatient(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyPatient(row.id).unwrap();
          } else {
            await deletePatient(row.id).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `Patient ${type === "restore" ? "Restored" : "Deleted"}!`,
            message: `${type === "restore" ? "Restored" : "Deleted"} Patient: ${row.patient_name}`,
          });
        } catch (err) {
          handleError(err, "Failed to update user.");
        }
      },
    });
  };

  const handleMultiple = (selectedItems, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} these Patients?`,
      title: `Patients ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(selectedItems);
          if (type === "restore") {
            await bulkRestorePatients(body).unwrap();
          } else if (type === "destroy") {
            await bulkDestroyPatients(body).unwrap();
          } else {
            await bulkDeletePatients(body).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `${type === "restore" ? "Restored" : "Deleted"} selected patients`,
            message: `${selectedItems.length} patients ${type === "restore" ? "restored" : "deleted"} successfully.`,
          });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  const headers = [
    {
      key: "system_number", label: "System No.", sortable: true, render: (row) => (
        <div className="text-xs whitespace-nowrap">
          {row.system_number}
        </div>
      )
    },
    {
      key: "patient_name",
      label: "Patient Details",
      sortable: true,
      render: (row) => (
        <div className="text-xs whitespace-nowrap">
          <div>
            <strong>Name:</strong> {row.patient_name}
          </div>
          <div className="mt-1">
            <strong>F/H:</strong> {row.father_husband_name}
          </div>
          <div className="mt-1">
            <span className="bg-green-100 uppercase font-bold  text-end text-green-700 px-2 mt-3 rounded-full text-[10px]">
              {row.patient_type}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "mobile", label: "Mobile", sortable: true, render: (row) => (
        <div className="text-xs whitespace-nowrap">
          {row.mobile}
        </div>
      )
    },
    {
      key: "Location", label: "Location", sortable: true, render: (row) => (
        <div className="text-xs whitespace-nowrap">
          {row.location}
        </div>
      )
    },
    {
      key: "patient_cnic", label: "Cnic", sortable: true, render: (row) => (
        <div className="text-xs whitespace-nowrap">
          {row.patient_cnic}
        </div>)
    },
    {
      label: "Unit - District",
      key: "unit",
      sortable: true,
      render: (row) => (
        <div className="text-xs whitespace-nowrap">
          <div>
            <span className="bg-yellow-200 dark:text-black text-end text-black-700 px-2 mt-3 font-semibold shadow-sm rounded-full text-[10px]">
              {row?.unit?.name} - {row?.unit?.district?.name}
            </span>
          </div>
        </div>
      ),
    },
  ];

  const actionBtnClass =
    "h-7 w-7 p-0 min-w-0 flex items-center justify-center rounded-md";

  const renderActions = (row) => (
    <div className="flex items-center gap-1">
      {!trashed ? (
        <>
          <Button
            onClick={() =>
              navigate(ROUTE_PATHS.PATIENT_CREATE, {
                state: { patient_details: row },
              })
            }
            title="Edit"
            icon={Edit3Icon}
            className={`${actionBtnClass} text-green-600 hover:bg-green-100`}
          />

          <Button onClick={() =>
            navigate(ROUTE_PATHS.PATIENT_DETAILS.replace(":system_number", row.system_number),
              {
                state: { patient_details: row },
              }
            )
          }
            title="View Details"
            icon={EyeIcon}
            className={`${actionBtnClass} text-blue-600 hover:bg-blue-100`}
          />

          <Button
            onClick={() =>
              navigate(ROUTE_PATHS.PATIENT_VISIT_LIST, {
                state: { patient_id: row.id }
              })
            }
            title="Patient Visits"
            icon={ClipboardListIcon}
            className={`${actionBtnClass} text-purple-600 hover:bg-purple-100`}
          />
        </>
      ) : (
        <Button
          onClick={() => handleSingle(row, "restore")}
          title="Restore"
          icon={RotateCcwIcon}
          className={`${actionBtnClass} text-green-600 hover:bg-green-100`}
        />
      )}

      <Button
        onClick={() =>
          handleSingle(row, trashed ? "destroy" : "delete")
        }
        title="Delete"
        icon={TrashIcon}
        className={`${actionBtnClass} text-red-600 hover:bg-red-100`}
      />
    </div>
  );

  return (
    <>
      <BreadCrumb title="Patients" />

      {/* Filters */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${showFilter
          ? "max-h-[500px] opacity-100 mb-4 translate-y-0"
          : "max-h-0 opacity-0 -translate-y-2"
          }`}
      >
        <FilterForm
          setShowFilter={setShowFilter}
          filtersData={filtersData}
          setFilter={setFiltersData}
          loading={isFetching}
          handleSubmit={handleFilter}
          handleReset={handleReset}
        /></div>
      {/* End of Filters */}
      <TableListing
        headers={headers}
        hasFilter={true}
        filterButtonAction={() => {
          setShowFilter(!showFilter)
        }}
        paginationData={patients}
        loading={isFetching}
        createButton
        paramName={"system_number"}
        paramCreateData={GenerateSystemNumber(patients?.total_items)}
        createLink={ROUTE_PATHS.PATIENT_CREATE}
        actions={renderActions}
        handleBulk={handleMultiple}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}

        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
      />
    </>
  );
};

export default PatientList;
